/**
 * ================================
 * DataRobot Web Form Tracking & Validation Script
 * ================================
 * 
 * This script is responsible for:
 * 
 * 1. **Munchkin Tracking (Marketo)**
 *    - Loads and initializes Marketo's Munchkin script for tracking.
 * 
 * 2. **Attribution Tracking**
 *    - Captures UTM parameters (campaign, source, medium, etc.).
 *    - Stores them in localStorage for 24 hours.
 *    - Populates hidden fields in forms with attribution data.
 * 
 * 3. **Email Validation**
 *    - Blocks free/disposable email domains.
 *    - Ensures only work emails are accepted.
 *    - Prevents form submission for invalid emails.
 * 
 * 4. **Marketo Form Enhancements**
 *    - Attaches event listeners to track form submission & validation.
 *    - Ensures correct form redirection after submission.
 *    - Sends error details to Google Tag Manager (GTM).
 * 
 * 5. **Link Attribution**
 *    - Appends tracking parameters to links pointing to `app.datarobot.com`.
 * 
 * 6. **Utility Functions**
 *    - UUID generation, localStorage management, delayed form polling.
 */

import { setItemWithConsent } from "./consentStorage";

/**
 * OneTrust category gating persistence of the attribution blob.
 *
 * The blob is campaign attribution feeding Marketo forms, so it sits with the
 * Marketo cookie (_mkto_trk) under Marketing. Only the persisted copy is gated;
 * the value is still returned in memory so form population within the visit is
 * unaffected.
 */
const ATTRIBUTION_STORAGE_CATEGORY = "C0007";

//-------------------------------
// MUNCHKIN SCRIPT (Marketo Tracking)
//-------------------------------

// testing the build

window.dataLayer = window.dataLayer || [];

(function() {
    var didInit = false;

    // Initialize Munchkin tracking if not already initialized
    function initMunchkin() {
      if (didInit === false && typeof window.Munchkin !== 'undefined') {
        didInit = true;
        Munchkin.init('229-XUK-217');
      }
    }

    // Load Marketo's Munchkin script
    var s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = '//munchkin.marketo.net/munchkin.js';
    s.onreadystatechange = function() {
      if (this.readyState == 'complete' || this.readyState == 'loaded') {
        initMunchkin();
      }
    };
    s.onload = initMunchkin;
    document.getElementsByTagName('head')[0].appendChild(s);

    // OneTrust Auto-Blocking holds this script until the visitor consents to C0007,
    // then releases it by injecting a NEW script element. That replacement does not
    // carry the onload handler assigned above, so initMunchkin() never fired and
    // Munchkin.init() was never called: munchkin.js loaded, window.Munchkin existed,
    // but Munchkin.initialized stayed false and _mkto_trk was never set. Poll for the
    // global as a fallback so initialisation survives the re-injection.
    var munchkinTries = 0;
    var munchkinPoll = setInterval(function () {
      munchkinTries++;
      if (typeof window.Munchkin !== 'undefined') {
        initMunchkin();
        clearInterval(munchkinPoll);
      } else if (munchkinTries > 60) {
        // ~30s. Consent was refused, or Marketo is unreachable. Stop watching.
        clearInterval(munchkinPoll);
      }
    }, 500);
  })();

//-------------------------------   
// ATTRIBUTION SCRIPT (UTM & Referrer Tracking)
//-------------------------------  

var ATTRIBUTION_MAP = [
    { name: 'campaign', parameter: 'utm_campaign', "default": '', api: 'utm_campaign', marketo: 'Campaign_Name__c' },
    { name: 'medium', parameter: 'utm_medium', "default": '', api: 'utm_medium', marketo: 'Campaign_Medium__c' },
    { name: 'source', parameter: 'utm_source', "default": '', api: 'utm_source', marketo: 'Campaign_Source__c' },
    { name: 'content', parameter: 'utm_content', "default": '', api: 'utm_content', marketo: 'Campaign_Content__c' },
    { name: 'term', parameter: 'utm_term', "default": '', api: 'utm_term', marketo: 'Campaign_Term__c' },
    { name: 'gclid', parameter: 'gclid', "default": '', api: 'gclid', marketo: 'Gclid__c' },
    { name: 'msclkid', parameter: 'msclkid', "default": '', api: 'msclkid', marketo: 'Microsoft_Click_ID__c' },
    { name: 'fbclid', parameter: 'fbclid', "default": '', api: 'fbclid', marketo: 'Facebook_Click_ID__c' },
    { name: 'li_fat_id', parameter: 'li_fat_id', "default": '', api: 'li_fat_id', marketo: 'Licid__c' }
];

// Retrieves URL parameters from the current page.
function getUrlParams() {
    return new URLSearchParams(window.location.search);
}

// Checks if attribution data is already stored in localStorage.
function hasAttributionLocalStorage() {
    return !!localStorage.getItem('attribution');
}

// Checks if any known attribution parameters exist in the URL.
function hasAttributionParameters() {
    return ATTRIBUTION_MAP.some(function (elem) {
        return getUrlParams().has(elem.parameter);
    });
}

// Extracts attribution data from URL parameters and referrer.
function getAttributionParameters() {
    var urlParams = getUrlParams();
    var referrer = document.referrer || 'direct';
    return ATTRIBUTION_MAP.reduce(function (acc, elem) {
        acc[elem.api] = urlParams.get(elem.parameter) || 
            (elem.name === 'medium' && !document.referrer ? '' : 
             elem.name === 'source' ? referrer : elem["default"]);
        return acc;
    }, {});
}

// Stores or updates attribution data in localStorage.
function setAttributionLocalStorage() {
    if (!hasAttributionLocalStorage() || hasAttributionParameters()) {
        var data = {
            value: getAttributionParameters(),
            expiration: Date.now() + 24 * 60 * 60 * 1000 // 24 hours from now
        };
        // Persisted only with consent; held until the visitor decides and dropped
        // if they refuse. The value is still returned either way, so form
        // population within this visit is unaffected. See misc/consentStorage.
        setItemWithConsent(
            window.localStorage,
            'attribution',
            JSON.stringify(data),
            ATTRIBUTION_STORAGE_CATEGORY
        );
        return data.value;
    }
}

// Retrieves stored attribution data or refreshes it if expired.
function getAttributionValues() {
    if (hasAttributionLocalStorage() && !hasAttributionParameters()) {
        var storedData = JSON.parse(localStorage.getItem('attribution'));
        if (Date.now() > storedData.expiration) {
            localStorage.removeItem('attribution');
            return setAttributionLocalStorage();
        }
        return storedData.value;
    }
    return setAttributionLocalStorage();
}

// Set hidden attribution fields in form
function setHiddenAttributionFields(form) {
    var attributionValues = getAttributionValues();
    var fields = ATTRIBUTION_MAP.reduce(function (acc, elem) {
        if (attributionValues[elem.api]) {
            acc[elem.marketo] = attributionValues[elem.api];
        }
        return acc;
    }, {});
    form.addHiddenFields(fields);
}

// Attribute links
function attributeLinks() {
    var links = document.querySelectorAll('[href*="app.datarobot.com"]');
    var attributionValues = getAttributionValues();
    links.forEach(function (link) {
        var url = new URL(link.href);
        var params = new URLSearchParams(url.search);
        ATTRIBUTION_MAP.forEach(function (elem) {
            if (attributionValues[elem.api]) {
                params.set(elem.parameter, attributionValues[elem.api]);
            }
        });
        link.href = url.origin + url.pathname + '?' + params.toString();
    });
}

// Clear expired localStorage items
function clearExpiredLocalStorage() {
    var storedData = JSON.parse(localStorage.getItem('attribution'));
    if (storedData && Date.now() > storedData.expiration) {
        localStorage.removeItem('attribution');
    }
}


function pollForForms(delay) {
    if (window.MktoForms2 !== undefined) {
        setMarketoFormListeners(window.MktoForms2);
    } else {
        setTimeout(pollForForms.bind(null, 2 * delay), delay);
    }
}

// Helper function to generate a UUID
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0,
            v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * helper function to push invalid Marketo field errors to GTM
 * @returns {undefined}
 */
function waitForError () {
    var element, input, mktoErrorMsg;
    // check for error message
    element = document.querySelector(".mktoErrorMsg");
    if (element) {
        mktoErrorMsg = element.textContent || element.innerText;
        // look for invalid input
        input = document.querySelector("input.mktoInvalid, .mktoInvalid input");
        window.dataLayer.push({
            "event": "mkto.form.error",
            "mkto.form.error.message": mktoErrorMsg,
            "gtm.element": input,
            "gtm.elementClasses": input && input.className || "",
            "gtm.elementId": input && input.id || "",
            "gtm.elementName": input && input.name || "",
            "gtm.elementTarget": input && input.target || ""
        });
    }
}

// Main execution
attributeLinks();
clearExpiredLocalStorage();

//-------------------------------   
// INVALID EMAIL DOMAINS SCRIPT
//-------------------------------   

var invalidDomains = ["@gmail.com", "@aol.com", "@hotmail.com", "@yahoo.com", "@ymail.com", "@0815.ru", "@0wnd.net", "@0wnd.org", "@10minutemail.co.za", "@10minutemail.com", "@123-m.com", "@1fsdfdsfsdf.tk", "@1pad.de", "@20minutemail.com", "@21cn.com", "@2fdgdfgdfgdf.tk", "@2prong.com", "@30minutemail.com", "@33mail.com", "@3trtretgfrfe.tk", "@4gfdsgfdgfd.tk", "@4warding.com", "@5ghgfhfghfgh.tk", "@6hjgjhgkilkj.tk", "@6paq.com", "@7tags.com", "@9ox.net", "@a-bc.net", "@agedmail.com", "@ama-trade.de", "@amilegit.com", "@amiri.net", "@amiriindustries.com", "@anonmails.de", "@anonymbox.com", "@antichef.com", "@antichef.net", "@antireg.ru", "@antispam.de", "@antispammail.de", "@armyspy.com", "@artman-conception.com", "@azmeil.tk", "@baxomale.ht.cx", "@beefmilk.com", "@bigstring.com", "@binkmail.com", "@bio-muesli.net", "@bobmail.info", "@bodhi.lawlita.com", "@bofthew.com", "@bootybay.de", "@boun.cr", "@bouncr.com"];

function isEmailGood(email) {
    if (!email) return false; // Handle empty email case
    var lowerCaseEmail = email.toLowerCase();
    return !invalidDomains.some(function(domain) {
        return lowerCaseEmail.endsWith(domain);
    });
}

if (typeof MktoForms2 !== "undefined") {
    var excludedFormIds = window.excludedFormIds || [1019, 1018, 1015, 1];

    MktoForms2.whenReady(function(form) {
        var currentFormId = form.getId();

        if (!excludedFormIds.includes(currentFormId)) {
            form.onValidate(function() {
                var email = form.vals().Email;
                var formElement = form.getFormElem();
                var emailElem = formElement.find("input[name='Email']");

                if (!isEmailGood(email)) {
                    form.submittable(false);
                    form.showErrorMessage('Must be a valid work email.', emailElem);
                } else {
                    form.submittable(true);
                }
            });

            form.onSubmit(function() {
                var email = form.vals().Email;
                if (!isEmailGood(email)) {
                    form.submittable(false);
                    return false; // Prevent form submission
                }
            });
        }
    });
}

function handleRedirectsForForm(form, uuid) {
    var baseURL;
    // Construct the redirect URL dynamically
    if(form.getId() === 1090) {
        baseURL = 'https://app.datarobot.com/account/oidc/redirect?initialDisplay=signup';
    } else if(form.getId() === 1120) {
        baseURL = 'https://app.datarobot.com/account/oidc/redirect?initialDisplay=signup&language=ja&region=app-jp';
    } else {
        return;
    }
    
    var url = new URL(baseURL);

    // Append the Form_Submission_Id__c as formSubmissionId
    url.searchParams.append('formSubmissionId', encodeURIComponent(uuid));
    window.location.href = url.toString();
}

//-------------------------------   
// FORM EVENT TRACKING (Marketo)
//-------------------------------   

// Attaches Marketo form listeners for tracking, validation, and redirection.
function setMarketoFormListeners(forms2) {
    forms2.whenReady(function(form) {
        setHiddenAttributionFields(form);

        window.dataLayer.push({
            "event": "mkto.form.ready",
            "mkto.form.id": form.getId(),
            "mkto.form.submittable": form.submittable(),
            "mkto.form.allFieldsFilled": form.allFieldsFilled(),
            "mkto.form.values": form.getValues()
        });

        form.onValidate(function handleValidate (valid) {
            window.dataLayer.push({
                "event": "mkto.form.validate",
                "mkto.form.valid": valid
            });
            // wait for the error message to appear
            setTimeout(waitForError, 0);
        });

        form.onSubmit(function handleSubmit (thisForm) {
            var button;
            button = thisForm.getFormElem().find("button[type=\"submit\"]");
            window.dataLayer.push({
                "event": "mkto.form.submit",
                "mkto.form.id": thisForm.getId(),
                "mkto.form.submittable": thisForm.submittable(),
                "mkto.form.allFieldsFilled": thisForm.allFieldsFilled(),
                "mkto.form.values": thisForm.getValues(),
                "mkto.form.button": {
                    "classes": button.attr("class"),
                    "text": button.text(),
                    "type": "submit"
                }
            });
        });

        if (form.getId() === 1090 || form.getId() === 1120) {
            // Generate the UUID and set it as the hidden field value
            var uuid = generateUUID();
            form.addHiddenFields({
                "Form_Submission_Id__c": uuid
            });

            form.onSuccess(function (values, followUpUrl) {
                window.dataLayer.push({
                    "event": "mkto.form.success",
                    "mkto.form.values": values,
                    "mkto.form.followUpUrl": followUpUrl
                });
                window.dataLayer.push({
                    'event': 'formSuccess',
                    'formSubmissionId': uuid
                });
                handleRedirectsForForm(form, uuid);
                return false;
            });
        } else {
            form.onSuccess(function handleSuccess (values, followUpUrl) {
                window.dataLayer.push({
                    "event": "mkto.form.success",
                    "mkto.form.values": values,
                    "mkto.form.followUpUrl": followUpUrl
                });
            });
        }

    });

    forms2.whenRendered(function handleRendered (form) {
        window.dataLayer.push({
            "event": "mkto.form.rendered",
            "mkto.form.id": form.getId(),
            "mkto.form.submittable": form.submittable(),
            "mkto.form.allFieldsFilled": form.allFieldsFilled(),
            "mkto.form.values": form.getValues()
        });
    });
}

pollForForms(10);
